import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Menu, X } from "lucide-react";
import { Button } from "../ui/button";
import { Logo } from "../Logo";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Careers", to: "/#values" },
  { label: "Open Roles", to: "/#roles" },
  { label: "Contact", to: "/contact" },
];

export function PublicHeader() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const goHash = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      if (loc.pathname !== "/") { navigate("/"); setTimeout(() => document.querySelector(to.slice(1))?.scrollIntoView({ behavior: "smooth" }), 250); }
      else document.querySelector(to.slice(1))?.scrollIntoView({ behavior: "smooth" });
    } else navigate(to);
  };

  const accountTarget = () => {
    if (user && user.role === "admin") return "/admin";
    if (user) return "/dashboard";
    return "/login";
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#101820]/95 backdrop-blur-md border-b border-[#C79A3B]/30" data-testid="public-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3 group" data-testid="public-nav-home">
          <Logo className="h-9 w-auto" />
          <span className="hidden lg:block text-[#E4C675]/80 text-[10px] leading-tight border-l border-[#C79A3B]/30 pl-3">Careers | Kerafoods<br /><span className="text-[#C79A3B]">No.1 South Indian Food Brand</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV.map((n) => (
            <button key={n.label} onClick={() => goHash(n.to)} data-testid={`public-nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
              className="text-[#FAF6EE]/80 hover:text-[#C79A3B] text-sm font-medium transition-colors">
              {n.label}
            </button>
          ))}
          <button onClick={() => navigate(accountTarget())} data-testid="public-nav-my-account"
            className="text-[#FAF6EE]/80 hover:text-[#C79A3B] text-sm font-medium transition-colors">My Account</button>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Button data-testid="public-nav-join-kera-cta" onClick={() => navigate(user ? accountTarget() : "/register")}
            className="bg-[#C79A3B] hover:bg-[#D4AF57] text-[#101820] font-bold rounded-full px-5 shadow-lg shadow-[#C79A3B]/20 transition-all hover:-translate-y-0.5">
            Join KERA
          </Button>
        </div>

        <button className="md:hidden text-[#FAF6EE]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#101820] border-t border-[#C79A3B]/20 px-4 py-4 space-y-3">
          {NAV.map((n) => (
            <button key={n.label} onClick={() => goHash(n.to)} className="block w-full text-left text-[#FAF6EE]/90 py-1">{n.label}</button>
          ))}
          <button onClick={() => { setOpen(false); navigate(accountTarget()); }} className="block w-full text-left text-[#FAF6EE]/90 py-1">My Account</button>
          <Button onClick={() => { setOpen(false); navigate(user ? accountTarget() : "/register"); }} className="w-full bg-[#C79A3B] text-[#101820] font-bold rounded-full">Join KERA</Button>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-[#101820] text-[#FAF6EE]/70 border-t border-[#C79A3B]/25 mt-16" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <Logo className="h-10 w-auto mb-3" />
          <p className="text-sm leading-relaxed">Authentic South Indian flavours, built for Ireland and Europe. Growing our retail, wholesale and foodservice distribution network.</p>
        </div>
        <div>
          <h4 className="text-[#C79A3B] font-semibold mb-3 text-sm uppercase tracking-wider">Explore</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-[#C79A3B]">Home</Link></li>
            <li><Link to="/#roles" className="hover:text-[#C79A3B]">Careers</Link></li>
            <li><Link to="/contact" className="hover:text-[#C79A3B]">Contact</Link></li>
            <li><Link to="/login" className="hover:text-[#C79A3B]">Candidate Login</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-[#C79A3B] font-semibold mb-3 text-sm uppercase tracking-wider">Legal</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/contact" className="hover:text-[#C79A3B]">Privacy Policy</Link></li>
            <li><span>GDPR & Data Protection</span></li>
          </ul>
        </div>
        <div>
          <h4 className="text-[#C79A3B] font-semibold mb-3 text-sm uppercase tracking-wider">Ireland</h4>
          <address className="text-sm not-italic leading-relaxed">
            Unit 7, The Business Centre,<br/>618 Northwest Logistics Park,<br/>Ballycoolin, Dublin 15,<br/>Ireland, D15V00Y
          </address>
        </div>
      </div>
      <div className="border-t border-[#C79A3B]/15 py-4 text-center text-xs text-[#FAF6EE]/50">
        © {new Date().getFullYear()} KERA Ireland. All rights reserved.
      </div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#F7F0E2]">
      <PublicHeader />
      <main className="flex-1 pt-16">{children}</main>
      <PublicFooter />
    </div>
  );
}
